import React from "react";
import { SubgridBodyColumn } from "../store/types";
import { SubgridCell } from "./SubgridCell";
import { Grid, GridProps } from "src/elements/Box/Box";
import { styled } from "styled-components";

export const SubgridRowElement = styled(Grid)`
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  top: 0;
`;

interface Props<T> extends Omit<GridProps, "columns"> {
  row: T;
  bodyColumns: SubgridBodyColumn<T>[];
  rowIndex: number;
  background?: string;
  height?: string;
}

function SubgridRowInner<T>(
  { row, bodyColumns, rowIndex, background, height, ...props }: Props<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  const { align: rowAlign, ...restProps } = props;

  return (
    <SubgridRowElement
      ref={ref}
      data-testid="subgrid-row"
      background={background}
      height={height}
      {...restProps}
    >
      {bodyColumns.map((bodyColumn, columnIndex) => {
        const typedRow = row as Record<string, string | number | boolean | null | undefined>;
        const value = bodyColumn.id === "index" ? rowIndex + 1 : typedRow[bodyColumn.id];

        if (bodyColumn.renderer) {
          return (
            <SubgridCell
              key={columnIndex}
              leftBorder={columnIndex === 0}
              justify={bodyColumn.justify}
              padding={bodyColumn.bodyPadding}
              align={bodyColumn.align || rowAlign}
            >
              {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                bodyColumn.renderer({ id: bodyColumn.id, row, value, index: rowIndex })
              }
            </SubgridCell>
          );
        }

        return (
          <SubgridCell
            key={columnIndex}
            leftBorder={columnIndex === 0}
            justify={bodyColumn.justify}
            align={bodyColumn.align || rowAlign}
            padding={bodyColumn.bodyPadding}
          >
            {value}
          </SubgridCell>
        );
      })}
    </SubgridRowElement>
  );
}

export const SubgridRow = React.memo(
  React.forwardRef(SubgridRowInner) as <T>(
    props: Props<T> & { ref?: React.Ref<HTMLDivElement> },
  ) => React.ReactElement,
  (prev, next) => {
    return (
      prev.row === next.row &&
      prev.bodyColumns === next.bodyColumns &&
      prev.rowIndex === next.rowIndex &&
      prev.background === next.background &&
      prev.height === next.height
    );
  },
);
